/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe } from "@angular/common";
import { Component } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { BehaviorSubject, first, Subject } from "rxjs";
import { FormActionsComponent, FormComponent, FormInputComponent, FormPasswordInputComponent, ModalComponent, requiredValidator } from "typedb-cloud-framework";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { SnackbarService } from "../../../service/snackbar.service";
import { IdentityAuthService } from "../../../service/authentication/identity-auth.service";
import { DialogResult } from "../../../service/dialog.service";
import { UserApi } from "../../../service/user/user-api.service";
import { ReauthenticateComponent, ReauthStage } from "../../../framework/reauthenticate/reauthenticate.component";

const AUTHENTICATION_FAILED = "Authentication failed, please try again";

@Component({
    selector: "tp-mfa-removal-dialog",
    templateUrl: "./mfa-removal-dialog.component.html",
    styleUrl: "./mfa-removal-dialog.component.scss",
    standalone: true,
    imports: [
        AsyncPipe, FormActionsComponent, FormsModule, ReactiveFormsModule, ModalComponent,
        MatFormFieldModule, MatInputModule, ReauthenticateComponent
    ],
})
export class MfaRemovalDialogComponent {

    readonly reauthForm = this.formBuilder.nonNullable.group({
        email: ["", [requiredValidator]],
        password: ["", [requiredValidator]],
    });
    reauthRequired = true;
    isSubmitting$ = new Subject<boolean>();
    dialogTitle$ = new BehaviorSubject<string>("Reauthenticate to disable MFA");

    constructor(
        private formBuilder: FormBuilder, private snackbar: SnackbarService, private auth: IdentityAuthService,
        private dialogRef: MatDialogRef<MfaRemovalDialogComponent, DialogResult>, private userApi: UserApi,
    ) {
        this.auth.currentUserStateChanges.pipe(first(user => !!user)).subscribe(user => {
            if (!this.reauthForm.value.email && !!user.email) this.reauthForm.patchValue({ email: user.email });
        });
    }

    disableMFA() {
        this.isSubmitting$.next(true);
        this.auth.unenrollMFA().then(() => {
            this.userApi.syncMfa().pipe(first()).subscribe();
            this.snackbar.success("MFA has been disabled");
            this.close("ok");
        }).catch(err => {
            this.isSubmitting$.next(false);
            this.snackbar.errorPersistent(err?.message || err?.toString() || AUTHENTICATION_FAILED)
        });
    }

    close(result?: DialogResult) {
        this.dialogRef.close(result);
    }

    reauthStageChanged(stage: ReauthStage) {
        if (stage === "reauthenticate") this.dialogTitle$.next("Reauthenticate to disable MFA");
        else if (stage === "verify-identity") this.dialogTitle$.next("Verify your identity");
        else if (stage === "complete") {
            this.dialogTitle$.next("Disable MFA");
            this.reauthRequired = false;
        }
    }
}
